import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, CheckCircle2, Loader2 } from 'lucide-react';

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI (GPT)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'emergent', label: 'Emergent (Universal Key)' },
  { value: 'custom', label: 'Custom / Self-hosted' },
];

/**
 * LLM providers sub-section of Admin → Integrations tab.
 * Extracted from the 584-line monolith in iter123.
 */
export const LLMProvidersSection = ({
  llms, showDialog, setShowDialog,
  form, setForm, editing, saving,
  onCreate, onEdit, onActivate, onSave, onDelete,
}) => (
  <div className="space-y-4">
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">LLM Providers</CardTitle>
          <CardDescription>
            Register one or more LLM providers. The provider marked &quot;Active&quot; powers all agent responses.
            Token costs feed plan pricing.
          </CardDescription>
        </div>
        <Button onClick={onCreate} className="gap-1" data-testid="add-llm-btn">
          <Plus className="w-4 h-4" /> Add LLM
        </Button>
      </CardHeader>
      <CardContent>
        {llms.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">
            No LLM providers configured. Currently using the default Emergent LLM key + Gemini 2.5 Flash.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {llms.map((l) => (
              <Card key={l.provider_id} className={`border ${l.is_active ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-200'}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {l.name}
                        {l.is_active && <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {l.provider} • {l.model_name}
                      </div>
                      <div className="text-xs text-slate-400 mt-1 font-mono">{l.api_key_masked || '****'}</div>
                    </div>
                    <div className="flex gap-1">
                      {!l.is_active && (
                        <Button size="sm" variant="outline" onClick={() => onActivate(l)} className="h-7 text-xs">
                          Activate
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onEdit(l)} className="h-7"><Edit className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(l)} disabled={l.is_active} className="h-7 text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 grid grid-cols-2 gap-2 mt-2 pt-2 border-t">
                    <span>Input: <strong>${(l.cost_per_1k_input_tokens || 0).toFixed(4)}</strong>/1K</span>
                    <span>Output: <strong>${(l.cost_per_1k_output_tokens || 0).toFixed(4)}</strong>/1K</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/* LLM Dialog */}
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit LLM Provider' : 'Add LLM Provider'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Display Name *</Label>
            <Input data-testid="llm-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., OpenAI GPT-5 Production" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Provider</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROVIDER_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Model *</Label>
              <Input data-testid="llm-model" value={form.model_name} onChange={(e) => setForm({ ...form, model_name: e.target.value })} placeholder="gpt-5.2 / claude-sonnet-4 / gemini-3-pro" />
            </div>
          </div>
          <div>
            <Label>API Key {editing && <span className="text-xs text-slate-400">(leave blank to keep current)</span>}</Label>
            <Input data-testid="llm-api-key" type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="sk-..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cost per 1K input tokens (USD)</Label>
              <Input data-testid="llm-cost-in" type="number" step="0.0001" value={form.cost_per_1k_input_tokens} onChange={(e) => setForm({ ...form, cost_per_1k_input_tokens: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Cost per 1K output tokens (USD)</Label>
              <Input data-testid="llm-cost-out" type="number" step="0.0001" value={form.cost_per_1k_output_tokens} onChange={(e) => setForm({ ...form, cost_per_1k_output_tokens: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
            <Label className="cursor-pointer">Mark as active (powers all agents immediately)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-llm-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {editing ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
);

export default LLMProvidersSection;
